import _ from 'lodash';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';

import ChangeAssignment from 'actions/conversation/change_assignment';
import connect from 'components/lib/connect';
import Conversation from 'models/conversation';
import ConversationAssignee from './conversation_assignee';
import CurrentAssignee from './current_assignee';
import CustomerAssignee from './customer_assignee';
import CustomerAssignmentButtonOptions from 'components/lib/assignment_menu/customer_assignment_button_options';
import getAssigneeStateForConversation from 'components/lib/assignment_menu/lib/get_assignee_state_for_conversation';
import i18n from 'scripts/i18n/i18n';
import OutsideClickHandler from 'components/common/utilities/outside_click_handler';
import registerHotkey from 'components/hotkeys/register_hotkey';
import SearchableAssignmentMenu from 'components/lib/assignment_menu/searchable_assignment_menu';
import SearchResult from './search_result';
import ShowReopenConversationModal from 'actions/conversation/show_reopen_conversation_modal';
import withShortcuts from 'scripts/presentation/decorators/keypress_shortcut_decorator';

export class SearchableConversationAssignment extends PureComponent {
  constructor(props) {
    super(props);
    this.state = { isMenuOpen: false };

    this._containerRef = React.createRef();

    _.bindAll(this, [
      'closeAssignmentMenu',
      'getAssigneeInfo',
      'handleKeyDown',
      'handleSelect',
      'handleToggleMenu',
      'renderAssignmentRow',
    ]);
  }

  render() {
    return (
      <div className="searchableConversationAssignment" ref={this._containerRef}>
        {this.renderMenu()}
        {this.renderAssignee()}
      </div>
    );
  }

  renderMenu() {
    if (!this.state.isMenuOpen) {
      return null;
    }

    return (
      <div className="searchableConversationAssignment-menu-block" data-aid="assignment-menu-block">
        <OutsideClickHandler innerRef={this._containerRef} onClickOutside={this.closeAssignmentMenu}>
          <CustomerAssignee
            className="searchableConversationAssignment-customerAssignee"
            onClose={this.closeAssignmentMenu}
          />
          <SearchableAssignmentMenu
            className="searchableConversationAssignment-menu"
            conversationId={this.props.conversationId}
            currentAssignee={this.props.conversationAssignee}
            onBlur={_.noop}
            renderRow={this.renderAssignmentRow}
            showSelfAssignmentResultsByDefault
            title={i18n.t('Assign To')}
          />
        </OutsideClickHandler>
      </div>
    );
  }

  renderAssignmentRow(hit) {
    let agentButtons = null;
    let inboxButtons = null;
    const { canManageCustomerAssignee } = this.props;

    if (canManageCustomerAssignee) {
      agentButtons = <CustomerAssignmentButtonOptions onClose={this.closeAssignmentMenu} {...hit} />;
      inboxButtons = <CustomerAssignmentButtonOptions onClose={this.closeAssignmentMenu} {...hit} />;
    }

    return (
      <SearchResult
        agentButtons={agentButtons}
        agentClassName="conversation-selectableSearchResults-agents"
        inboxButtons={inboxButtons}
        inboxClassName="conversation-selectableSearchResults-inboxes"
        key={`${hit.routingGroupId}${_.get(hit, 'agentId', '')}`}
        onAgentSelect={canManageCustomerAssignee ? _.noop : this.handleSelect}
        onInboxSelect={canManageCustomerAssignee ? _.noop : this.handleSelect}
        {...hit}
      />
    );
  }

  renderAssignee() {
    const {
      conversationAssignee,
      conversationId,
      customerAssignee,
      hideAssigneeTooltips,
      renderAssignee: renderAssigneeProp,
    } = this.props;
    const className = 'searchableConversationAssignment-assignee';

    if (renderAssigneeProp) {
      return renderAssigneeProp({
        className,
        conversationAssignee,
        conversationId,
        customerAssignee,
        hideTooltips: hideAssigneeTooltips,
        onClick: this.handleToggleMenu,
      });
    }

    if (!conversationId) {
      return (
        <CurrentAssignee
          className={classnames({ 'currentAssignee-customerAssignee': !_.isEmpty(customerAssignee) }, className)}
          onClick={this.handleToggleMenu}
          {...this.getAssigneeInfo()}
        />
      );
    }

    return (
      <ConversationAssignee
        className={className}
        conversationAssignee={conversationAssignee}
        customerAssignee={customerAssignee}
        hideTooltips={this.props.hideAssigneeTooltips}
        onClick={this.handleToggleMenu}
        textLabel={i18n.t('Assigned to')}
      />
    );
  }

  getAssigneeInfo() {
    const { conversationAssignee, conversationId, customerAssignee } = this.props;
    let assignee = conversationAssignee || {};
    assignee.textLabel = i18n.t('Assigned to');

    if (!conversationId && !_.isEmpty(customerAssignee)) {
      assignee = customerAssignee;
      assignee.textLabel = customerAssignee.agentName ? i18n.t('Dedicated Hero') : i18n.t('Hero Inbox');
    }

    return assignee;
  }

  closeAssignmentMenu() {
    this.setState({ isMenuOpen: false }, () => {
      this.props.onMenuOpenChange && this.props.onMenuOpenChange(false);
    });
  }

  handleSelect(selected) {
    this.props.onSelect(selected);
    this.closeAssignmentMenu();
  }

  componentDidUpdate() {
    if (this.state.isMenuOpen && !this._escapeListenerAttached) {
      document.addEventListener('keydown', this.handleKeyDown, true);
      this._escapeListenerAttached = true;
    } else if (!this.state.isMenuOpen && this._escapeListenerAttached) {
      document.removeEventListener('keydown', this.handleKeyDown, true);
      this._escapeListenerAttached = false;
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown, true);
  }

  handleKeyDown(evt) {
    if (evt.key === 'Escape' && this.state.isMenuOpen) {
      evt.stopPropagation();
      this.closeAssignmentMenu();
    }
  }

  handleToggleMenu(evt) {
    evt && evt.preventDefault();

    if (!this.props.disableMenu) {
      this.setState(
        prevState => ({ isMenuOpen: !prevState.isMenuOpen }),
        () => {
          this.props.onMenuOpenChange && this.props.onMenuOpenChange(this.state.isMenuOpen);
        }
      );
    }

    this.props.onToggleMenu && this.props.onToggleMenu();
  }
}

SearchableConversationAssignment.propTypes = {
  canManageCustomerAssignee: PropTypes.bool,
  conversationId: PropTypes.string,
  conversationAssignee: PropTypes.shape({
    agentId: PropTypes.string,
    agentName: PropTypes.string,
    avatarImage: PropTypes.string,
    groupId: PropTypes.string,
    groupName: PropTypes.string,
    isAssigneeCurrentAgent: PropTypes.bool,
    textLabel: PropTypes.string,
  }),
  customerAssignee: PropTypes.shape({
    agentId: PropTypes.string,
    agentName: PropTypes.string,
    avatarImage: PropTypes.string,
    groupId: PropTypes.string,
    groupName: PropTypes.string,
    isAssigneeCurrentAgent: PropTypes.bool,
  }),
  disableMenu: PropTypes.bool,
  hideAssigneeTooltips: PropTypes.bool,
  isConversationClosed: PropTypes.bool,
  onMenuOpenChange: PropTypes.func,
  onSelect: PropTypes.func,
  onToggleMenu: PropTypes.func,
  renderAssignee: PropTypes.func,
};

export const ConversationAssignmentWithShortcuts = withShortcuts(SearchableConversationAssignment, [
  registerHotkey({
    key: 'alt+a',
    callback: component => component.handleToggleMenu(),
    group: 'Conversation',
    label: 'Change assignee',
  }),
]);

function mapStateToMenuProps({ getProvider, isFeatureEnabled }, { conversationId }) {
  const conversation = getProvider('conversations').findBy({ id: conversationId });
  const isConversationClosed = _.get(conversation, 'status') === Conversation.Status.CLOSED;

  const { conversationAssignee, customerAssignee } = getAssigneeStateForConversation(
    { getProvider, isFeatureEnabled },
    conversationId
  );

  return {
    canManageCustomerAssignee: isFeatureEnabled('customer:dedicatedHero') && isFeatureEnabled('dedicatedHero'),
    conversationAssignee,
    customerAssignee,
    disableMenu: isConversationClosed,
    hideAssigneeTooltips: isFeatureEnabled('fullHeightSidePanel'),
  };
}

function mapExecuteToMenuProps(executeAction) {
  return {
    onSelect: ({ agentId, routingGroupId }) => {
      executeAction(ChangeAssignment, {
        assignee: { agentId, routingGroupId },
      });
    },
    onToggleMenu: () => executeAction(ShowReopenConversationModal),
  };
}

function mergeProps(stateProps, execProps, ownProps) {
  const props = {
    ...stateProps,
    ...execProps,
    ...ownProps,
  };

  if (!stateProps.disableMenu) {
    delete props.onToggleMenu;
  }

  return props;
}

const SearchableConversationAssignmentContainer = connect(
  mapStateToMenuProps,
  mapExecuteToMenuProps,
  mergeProps
)(ConversationAssignmentWithShortcuts);

SearchableConversationAssignmentContainer.propTypes = {
  conversationId: PropTypes.string,
  renderAssignee: PropTypes.func,
};

export default SearchableConversationAssignmentContainer;
